import React, { useState, useEffect } from 'react';
import { Field, Label, Input, Switch, Listbox } from '@headlessui/react';
import clsx from 'clsx';

const referralOptions = [
  'Website der Allianz Foundation',
  'Social Media (Instagram, Facebook, LinkedIn etc.)',
  'Newsletter',
  'Empfehlung von Freunden oder Familie',
  'Empfehlung durch eine Organisation oder Institution (z.B. Schule, Uni, Verein)',
  'Empfehlung durch die Allianz',
  'Presse/Medienbericht',
  'Veranstaltung/Event',
  'Online-Suche (z.B. Google)',
  'Frühere Teilnahme',
  'Sonstiges (bitte angeben)'
];

const Step4 = ({ next, back, data }) => {
  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState({});
  const [contactConsent, setContactConsent] = useState(formData.contactConsent || false);
  const [infoConsent, setInfoConsent] = useState(formData.infoConsent || false);
  const [newsletterSubscription, setNewsletterSubscription] = useState(formData.newsletterSubscription || false);
  const [showAgencyId, setShowAgencyId] = useState(false);
  const [referralSource, setReferralSource] = useState(formData.referralSource || '');
  const [otherReferralSource, setOtherReferralSource] = useState(formData.otherReferralSource || '');

  useEffect(() => {
    // Check if organization contains "ABV" or "Allianz"
    if (formData.NominatorInstitution) {
      const org = formData.NominatorInstitution.toLowerCase();
      setShowAgencyId(org.includes('abv') || org.includes('allianz'));
    } else {
      setShowAgencyId(false);
    }
  }, [formData.NominatorInstitution]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors((prevErrors) => {
        const { [name]: removedError, ...rest } = prevErrors;
        return rest;
      });
    }

    // Check if organization field contains "ABV" or "Allianz"
    if (name === 'NominatorInstitution') {
      const org = value.toLowerCase();
      setShowAgencyId(org.includes('abv') || org.includes('allianz'));
    }

    // Validate telephone on change
    if (name === 'NominatorTelephone') {
      if (!/^(?:\+|00)\d+$/.test(value)) {
        setErrors((prevErrors) => ({ ...prevErrors, NominatorTelephone: 'Die Telefonnummer muss mit + oder 00 beginnen und nur Ziffern enthalten' }));
      } else {
        setErrors((prevErrors) => {
          const { NominatorTelephone, ...rest } = prevErrors;
          return rest;
        });
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.Nominator) newErrors.Nominator = 'Vollständiger Name ist erforderlich';
    if (!formData.NominatorRelationship) newErrors.NominatorRelationship = 'Was ist deine Beziehung zu der nominierten Person?';
    if (!formData.NominatorEmail) {
      newErrors.NominatorEmail = 'Kontakt-E-Mail ist erforderlich';
    } else if (!/\S+@\S+\.\S+/.test(formData.NominatorEmail)) {
      newErrors.NominatorEmail = 'E-Mail-Adresse ist ungültig';
    }
    // Telephone field is now optional - only validate format if provided
    if (formData.NominatorTelephone && !/^(?:\+|00)\d+$/.test(formData.NominatorTelephone)) {
      newErrors.NominatorTelephone = 'Die Telefonnummer muss mit + oder 00 beginnen und nur Ziffern enthalten';
    }
    if (!contactConsent) newErrors.contactConsent = 'Das Einverständnis ist erforderlich!';

    // Validate referral source - make it required
    if (!referralSource) newErrors.referralSource = 'Bitte gib an, wie du auf den Movers of Tomorrow Award aufmerksam geworden bist';

    // Validate other referral source if "Sonstiges" is selected
    if (referralSource === 'Sonstiges (bitte angeben)' && !otherReferralSource) {
      newErrors.otherReferralSource = 'Bitte gib an, wie du auf den Movers of Tomorrow Award aufmerksam geworden bist';
    }

    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      next({
        ...formData,
        contactConsent,
        infoConsent,
        newsletterSubscription,
        referralSource,
        otherReferralSource,
        agencyId: showAgencyId ? formData.agencyId : null
      });
    }
  };

  const handleReferralChange = (value) => {
    setReferralSource(value);

    // Clear errors if any
    if (errors.referralSource) {
      setErrors((prevErrors) => {
        const { referralSource, ...rest } = prevErrors;
        return rest;
      });
    }

    // Clear other referral source if not "Sonstiges"
    if (value !== 'Sonstiges (bitte angeben)') {
      setOtherReferralSource('');
    }
  };

  return (
    <div className="step text-white flex flex-col gap-12">
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-bold">Informationen zu Dir</h2>
        <div className="space-y-4">
          <Field className="relative">
            <Input
              id="Nominator"
              name="Nominator"
              placeholder="Vollständiger Name"
              value={formData.Nominator}
              onChange={handleChange}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            {errors.Nominator && <p className="error px-6">{errors.Nominator}</p>}
          </Field>

          <Field className="relative">
            <Input
              id="NominatorRelationship"
              name="NominatorRelationship"
              placeholder="Was ist deine Beziehung zu der nominierten Person?"
              value={formData.NominatorRelationship}
              onChange={handleChange}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            {errors.NominatorRelationship && <p className="error px-6">{errors.NominatorRelationship}</p>}
          </Field>

          <Field className="relative">
            <Input
              id="NominatorEmail"
              name="NominatorEmail"
              type="email"
              placeholder="E-Mail"
              value={formData.NominatorEmail}
              onChange={handleChange}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
            {errors.NominatorEmail && <p className="error px-6">{errors.NominatorEmail}</p>}
          </Field>

          <Field className="relative">
            <Input
              id="NominatorTelephone"
              name="NominatorTelephone"
              placeholder="Telefon (optional)"
              value={formData.NominatorTelephone}
              onChange={handleChange}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
              onKeyPress={(e) => {
                const regex = /^[0-9+\b]+$/; 
                if (!regex.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {errors.NominatorTelephone && <p className="error px-6">{errors.NominatorTelephone}</p>}
          </Field>

          <Field className="relative">
            <Input
              id="NominatorInstitution"
              name="NominatorInstitution"
              placeholder="Organisation/Institution (falls zutreffend)"
              value={formData.NominatorInstitution}
              onChange={handleChange}
              className={clsx(
                'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
              )}
            />
          </Field>

          {/* Conditional Agency ID field */}
          {showAgencyId && (
            <Field className="relative">
              <Input
                id="agencyId"
                name="agencyId"
                placeholder="Agenturnummer bzw. Organisationseinheit"
                value={formData.agencyId || ''}
                onChange={handleChange}
                className={clsx(
                  'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                  'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                )}
              />
              {errors.agencyId && <p className="error px-6">{errors.agencyId}</p>}
              <p className="mt-4 text-white/80 text-sm/5 px-6"><strong>Hinweis:</strong> Die Angabe ist freiwillig. Mit der Eintragung willigst Du ein, dass ausschließlich die von Dir angegebene Agenturnummer bzw. Organisationseinheit an die Allianz Beratungs- und Vertriebs- AG weitergeleitet wird. Es werden keine personenbezogenen Daten übermittelt. Du kannst Deine Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen, ohne dass Dir daraus Nachteile entstehen.</p>
            </Field>
          )}

          {/* Referral source dropdown */}
          <Field className="relative pt-6">
            <Label className="block text-lg font-medium">
              Wie bist Du auf den Movers of Tomorrow Award aufmerksam geworden?
            </Label>
            <div className="relative mt-2">
              <Listbox value={referralSource} onChange={handleReferralChange}>
                <div className="relative">
                  <Listbox.Button className={clsx(
                    'relative w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white text-left',
                    'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple',
                    errors.referralSource ? 'ring-1 ring-red-500' : ''
                  )}>
                    <span className={`block truncate ${!referralSource ? "text-white/80" : ""}`}>
                      {referralSource || "Bitte wählen..."}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-6">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-white">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </Listbox.Button>

                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-black border border-white/20 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {referralOptions.map((option, optionIdx) => (
                      <Listbox.Option
                        key={optionIdx}
                        className={({ active }) =>
                          clsx(
                            active ? 'bg-white/10 text-white' : 'text-white',
                            'relative cursor-pointer select-none py-2 pl-6 pr-9'
                          )
                        }
                        value={option}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={clsx(selected ? 'font-medium' : 'font-normal', 'block truncate')}>
                              {option}
                            </span>
                            {selected ? (
                              <span className={clsx(active ? 'text-white' : 'text-purple', 'absolute inset-y-0 right-0 flex items-center pr-4')}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                </svg>
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
              {errors.referralSource && <p className="error px-6">{errors.referralSource}</p>}
            </div>
          </Field>

          {/* Conditional input field for "Sonstiges" */}
          {referralSource === 'Sonstiges (bitte angeben)' && (
            <Field className="relative">
              <Input
                id="otherReferralSource"
                name="otherReferralSource"
                placeholder="Bitte spezifizieren..."
                value={otherReferralSource}
                onChange={(e) => {
                  setOtherReferralSource(e.target.value);
                  if (errors.otherReferralSource) {
                    setErrors((prevErrors) => {
                      const { otherReferralSource, ...rest } = prevErrors;
                      return rest;
                    });
                  }
                }}
                className={clsx(
                  'block w-full border-none bg-white/5 hover:bg-white/10 focus:bg-white/10 pt-3.5 pb-4 px-6 text-lg/7 text-white mt-2 placeholder:text-white/80',
                  'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-purple'
                )}
              />
              {errors.otherReferralSource && <p className="error px-6">{errors.otherReferralSource}</p>}
            </Field>
          )}

          <div className="pt-6 md:pt-12">
            <h2 className="text-2xl font-bold">Einverständnis und Vereinbarung</h2>
          </div>

          <Field className="relative flex items-center pt-4">
            <div className="flex-none w-1/12">
              <Switch
                checked={contactConsent}
                onChange={setContactConsent}
                className={clsx(
                  'relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-200 ease-in-out',
                  contactConsent ? 'bg-purple' : 'bg-white/20'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out',
                    contactConsent ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </Switch>
            </div>
            <div className="basis-full flex flex-col">
              <Label className="ml-5"><p className="text-lg font-medium text-white leading-5 cursor-pointer">Ich habe das Einverständnis der nominierten Person und ggf. einer/eines Erziehungsberechtigten über die Weitergabe seiner/ihrer Daten im Rahmen der Nominierung.</p></Label>
              {errors.contactConsent && <p className="error px-5">{errors.contactConsent}</p>}
              <div className="download ml-5 mt-5">
                <a href="https://res.cloudinary.com/allianz-foundation/image/upload/v1744365491/NEU_240708_Moversof_Tomorrow_Einverstaendniserklaerung_9639889c71.pdf" target="blank"
                  className="flex gap-3 font-semibold text-sm leading-none hover:text-purple">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="size-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Einverständniserklärung für Deine Unterlagen
                </a>
              </div>
            </div>
          </Field>

          <Field className="relative flex items-center pt-4">
            <div className="flex-none w-1/12">
              <Switch
                checked={infoConsent}
                onChange={setInfoConsent}
                className={clsx(
                  'relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-200 ease-in-out',
                  infoConsent ? 'bg-purple' : 'bg-white/20'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out',
                    infoConsent ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </Switch>
            </div>
            <div className="basis-full flex flex-col">
              <Label className="ml-5"><p className="text-lg font-medium text-white leading-5 cursor-pointer">Ich möchte gerne über den weiteren Verlauf des Movers of Tomorrow Awards informiert werden.</p></Label>
            </div>
          </Field>

          <Field className="relative flex items-center pt-4">
            <div className="flex-none w-1/12">
              <Switch
                checked={newsletterSubscription}
                onChange={setNewsletterSubscription}
                className={clsx(
                  'relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-200 ease-in-out',
                  newsletterSubscription ? 'bg-purple' : 'bg-white/20'
                )}
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    'inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out',
                    newsletterSubscription ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </Switch>
            </div>
            <div className="basis-full flex flex-col">
              <Label className="ml-5"><p className="text-lg font-medium text-white leading-5 cursor-pointer">Ich möchte gerne den Newsletter der Allianz Foundation erhalten.</p></Label>
            </div>
          </Field>

        </div>
      </div>

      <nav className="progress-nav">
        <div className="inner flex h-full items-center justify-between">
          <button onClick={back} className="flex gap-3 items-center hover:text-purple">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
            </svg>
            <span className="block -mt-1 font-semibold tracking-wide">Zurück</span>
          </button>
          <button onClick={handleSubmit} className="primary-form">
            <span className="font-semibold tracking-wide -mt-0.5 block text-lg">Überprüfen</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Step4;